import type { TransactionSummary, CalendarDay, Transaction } from '../../../services/transactionApi';
import type { Bill } from '../../../services/billApi';
import type { Goal as ApiGoal } from '../../../services/goalApi';
import { goalProgress } from '../../../utils/goals';
import { monthKey } from '../../../utils/calendar';
import { projectBillsIntoMonth } from '../../../utils/recurrence';
import type { DashboardHomeProps } from './DashboardHome';
import type { DailySpend, MonthlyExpense } from './dashboard.types';

/** Everything the home reads, as the API returns it — all for the month in focus. */
export interface HomeSources {
  summary: TransactionSummary;
  incomeEntries: number;
  expenseEntries: number;
  calendar: CalendarDay[];
  /** MONTHS_SHOWN months, zero-filled, the last one being the month in focus */
  monthly: MonthlyTotals[];
  bills: Bill[];
  goals: ApiGoal[];
  /** the context's monthly budget; 0 = none */
  budgetLimit: number;
  /** the month's latest entries, newest first */
  recent?: Transaction[];
}

export type HomeData = Omit<
  DashboardHomeProps,
  'currency' | 'groupsCard' | 'onSeeBills' | 'onSeeGoals' | 'onSeeTransactions' | 'onSetBudget'
>;

export interface MonthlyTotals {
  /** "2026-09" */
  month: string;
  income: number;
  expense: number;
}

export const MONTHS_SHOWN = 6;
const BILLS_SHOWN = 5;
const GOALS_SHOWN = 3;
export const RECENT_SHOWN = 5;

const parseMonth = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  return { year, monthNumber };
};

/** How many months before now the month in focus sits; 0 for the current month. */
export const monthsBack = (month: string, now: Date = new Date()): number => {
  const { year, monthNumber } = parseMonth(month);
  return Math.max(0, (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - monthNumber));
};

/**
 * The dates the month in focus spans: its first day through today, or
 * through its last day once it is over.
 */
export const monthRange = (month: string, now: Date = new Date()): { startDate: string; endDate: string } => {
  const { year, monthNumber } = parseMonth(month);
  const lastDay = month === monthKey(now) ? now.getDate() : new Date(year, monthNumber, 0).getDate();
  return { startDate: `${month}-01`, endDate: `${month}-${String(lastDay).padStart(2, '0')}` };
};

/** `span` months ending at the month in focus, zero-filled where the API had no row. */
export const monthsEndingAt = (monthly: MonthlyTotals[], month: string, span = MONTHS_SHOWN): MonthlyTotals[] => {
  const { year, monthNumber } = parseMonth(month);
  const byMonth = new Map(monthly.map((entry) => [entry.month, entry]));
  return Array.from({ length: span }, (_, index) => {
    const key = monthKey(new Date(year, monthNumber - 1 - (span - 1 - index), 1));
    const entry = byMonth.get(key);
    return { month: key, income: Number(entry?.income) || 0, expense: Number(entry?.expense) || 0 };
  });
};

/**
 * Turns the API's month into the cards' numbers. Pure, so the arithmetic
 * (pace, projection, delta) is testable without a network. A month that is
 * over is taken as it was: whole, unprojected, with nothing committed.
 */
export const buildHomeData = (sources: HomeSources, now: Date = new Date(), month: string = monthKey(now)): HomeData => {
  const { year, monthNumber } = parseMonth(month);
  const closed = month !== monthKey(now);
  const daysInMonth = new Date(year, monthNumber, 0).getDate();
  const dayOfMonth = closed ? daysInMonth : now.getDate();
  const income = Number(sources.summary.totalIncome) || 0;
  const expenses = Math.abs(Number(sources.summary.totalExpenses) || 0);

  // Pace: what the month has cost per day so far, stretched to its last day.
  const projectMonth = (spentSoFar: number) =>
    closed || dayOfMonth <= 0 ? spentSoFar : (spentSoFar / dayOfMonth) * daysInMonth;

  const before = sources.monthly.slice(0, -1);
  const previous = before[before.length - 1];
  const expensesDelta = previous && previous.expense > 0 ? (expenses - previous.expense) / previous.expense : undefined;

  const months = sources.monthly.map((entry, index) => ({
    month: entry.month,
    spent: entry.expense,
    projected: !closed && index === sources.monthly.length - 1 ? projectMonth(entry.expense) : undefined,
  }));

  // The month's run: days without movement carry the previous total.
  const byDay = new Map<number, { income: number; expense: number }>();
  sources.calendar.forEach((day) => {
    const date = String(day.date).slice(0, 10);
    if (date.slice(0, 7) !== month) return;
    byDay.set(Number(date.slice(8, 10)), { income: Number(day.income) || 0, expense: Number(day.expense) || 0 });
  });
  const actual: DailySpend[] = [];
  let cumulative = 0;
  for (let day = 1; day <= dayOfMonth; day += 1) {
    cumulative += byDay.get(day)?.expense ?? 0;
    actual.push({ day, cumulative });
  }
  let incomeDay: number | undefined;
  let incomeAmount = 0;
  byDay.forEach((totals, day) => {
    if (totals.income > incomeAmount) {
      incomeAmount = totals.income;
      incomeDay = day;
    }
  });

  // Committed this month: open bills due in it plus projected recurrences —
  // the same arithmetic the Planning page shows. A month that is over has
  // nothing left to commit.
  const openBills = sources.bills.filter((bill) => bill.status === 'open');
  const committed = closed
    ? 0
    : [
        ...openBills.filter((bill) => bill.dueDate.slice(0, 7) === month),
        ...projectBillsIntoMonth(openBills, month),
      ].reduce((sum, bill) => sum + (Number(bill.amount) || 0), 0);

  const bills = [...openBills]
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .slice(0, BILLS_SHOWN)
    .map((bill) => ({
      id: bill.id,
      name: bill.installmentNumber && bill.installmentTotal ? `${bill.description} · ${bill.installmentNumber}/${bill.installmentTotal}` : bill.description,
      dueDate: bill.dueDate, amount: Number(bill.amount), currency: bill.currency }));

  // Habits show this month's progress; events, the whole journey.
  const goals = sources.goals
    .filter((goal) => goal.status === 'active')
    .slice(0, GOALS_SHOWN)
    .map((goal) => {
      const habit = goal.goalType === 'recurring';
      const saved = habit ? Number(goal.monthContributed ?? 0) : Number(goal.currentAmount) || 0;
      const target = habit ? Number(goal.monthlyTarget ?? 0) : Number(goal.targetAmount ?? 0);
      return {
        id: goal.id,
        name: goal.name,
        progress: goalProgress(saved, target).ratio,
        habit,
        saved,
        target,
        currency: goal.currency,
        deadline: goal.targetDate ?? undefined,
      };
    });

  const categories = sources.summary.categories.map((category) => ({
    key: category.category,
    spent: Math.abs(Number(category.amount) || 0),
  }));

  return {
    month,
    summary: { income, expenses, committed, budget: sources.budgetLimit, dayOfMonth, daysInMonth, closed },
    incomeEntries: sources.incomeEntries,
    expenseEntries: sources.expenseEntries,
    expensesDelta,
    bills,
    goals,
    categories,
    months,
    recent: sources.recent ?? [],
    cumulative: {
      daysInMonth,
      today: dayOfMonth,
      actual,
      projectedEnd: projectMonth(cumulative),
      incomeDay,
      incomeAmount: incomeDay !== undefined ? incomeAmount : undefined,
      budget: sources.budgetLimit > 0 ? sources.budgetLimit : undefined,
      closed,
    },
  };
};

/**
 * The dotted line of "Despesas por mês": the mean of the closed months that
 * saw any spending. A month at zero is a month without records — a new
 * user's history, not a frugal one — and would only drag the mean down.
 */
export const previousMonthsAverage = (months: MonthlyExpense[]): { average: number; count: number } => {
  const spent = months.slice(0, -1).filter((month) => month.spent > 0);
  const total = spent.reduce((sum, month) => sum + month.spent, 0);
  return { average: spent.length ? total / spent.length : 0, count: spent.length };
};
